import { ReactComponent as MoreIcon } from '@paradigm/icons/src/mailru/navigation/more_vertical.svg';
import React, { useCallback, useEffect, VFC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { requestArchiveInfo, setArchiveCurrentFolder, setArchiveItemActive } from 'reactApp/modules/viewer/viewer.module';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { IArchiveItem } from 'reactApp/modules/viewer/viewer.types';
import { RootState } from 'reactApp/store';
import { Loader } from 'reactApp/ui/Loader/Loader';
import { Breadcrumbs } from 'reactApp/ui/Mobile/Breadcrumbs/Breadcrumbs';
import { ItemRow } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerArchive/ItemRow';
import { ItemViewer } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerArchive/ItemViewer';

import styles from './MobileViewerArchive.css';
import {EmptyFolder} from "reactApp/ui/Mobile/MobileViewer/MobileViewerArchive/EmptyFolder";

interface IMobileViewerArchiveProps {
    item: CloudFile;
    showBackButton?: boolean;
    storage: EStorageType;
    onClose: () => void;
    onShow: () => void;
    openToolbarOptions?: () => void;
}

export const MobileViewerArchive: VFC<IMobileViewerArchiveProps> = ({
    showBackButton = true,
    openToolbarOptions,
    onClose,
    onShow,
    item,
    storage,
}) => {
    const dispatch = useDispatch();
    const archiveInfo = useSelector(ViewerSelectors.getArchiveInfo);
    const activeItem = useSelector(ViewerSelectors.getArchiveActiveItem);
    const items = useSelector(ViewerSelectors.getCurrentFolderArchiveItems);
    const breadcrumbs = useSelector((state: RootState) => ViewerSelectors.getArchiveBreadcrumbs(state, item?.name));

    useEffect(() => {
        onShow();
        return () => {
            onClose();
        };
        /**
         * Дизайблим специально, чтобы имитировать didMount didUnmount
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(requestArchiveInfo({ storage, id: item.id }));
    }, [dispatch, item.id, storage]);

    const handleGo = useCallback(
        (path?: string) => {
            if (typeof path === 'undefined') {
                onClose();
            } else {
                dispatch(setArchiveCurrentFolder(path?.replace(/^\//, '')));
            }
        },
        [dispatch, onClose]
    );

    const handleClick = useCallback(
        (item: IArchiveItem) => {
            if (item.isFolder) {
                handleGo(item?.path);
            } else {
                dispatch(setArchiveItemActive(item?.path));
            }
        },
        [dispatch, handleGo]
    );

    /**
     * Когда есть активный элемент (не папка) отображаем его в ItemViewer
     */
    if (activeItem) {
        return <ItemViewer storage={storage} archiveId={archiveInfo?.archiveId || ''} item={activeItem} />;
    }

    return (
        <div className={styles.archive}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarBreadcrumbs}>
                    <Breadcrumbs
                        showBackButton={showBackButton}
                        name={breadcrumbs[breadcrumbs.length - 1]?.text || ''}
                        onClickBackButton={() => handleGo(breadcrumbs[breadcrumbs.length - 2]?.id)}
                    />
                </div>
                {breadcrumbs.length === 1 && (
                    <div className={styles.toolbarAction} onClick={openToolbarOptions}>
                        <MoreIcon width={20} height={20} fill="currentColor" />
                    </div>
                )}
            </div>
            <div className={styles.archiveContent}>
                {archiveInfo?.isLoaded ? (
                    items.length ? items.map((cloudItem) => (
                        <ItemRow
                            key={cloudItem.path}
                            item={cloudItem}
                            storage={storage}
                            archiveId={archiveInfo?.archiveId || ''}
                            onClick={handleClick}
                        />
                    )) : <EmptyFolder />
                ) : <Loader centered />}
            </div>
        </div>
    );
};
